import React, { useEffect, useState, useCallback } from 'react';
import { RotateCw, Loader2 } from 'lucide-react';
import { GlassButton, GlassBadge } from '../ui';
import { getJourneyEvents, getProjectJourneyEvents, JourneyTriggerEvent } from '../../services/api';

interface Props {
    campaignId?: string;
    journeyId?: string;
}

// Outcome -> GlassBadge variant, per CLAUDE.md's design system (no hardcoded
// colours) and the spec's CSS Notes mapping.
function outcomeBadgeVariant(outcome: string): 'success' | 'default' | 'warning' | 'danger' {
    if (outcome === 'enrolled') return 'success';
    if (outcome === 'duplicate' || outcome === 'no_match') return 'default';
    if (outcome === 'suppressed' || outcome === 'capped' || outcome === 'inactive_target' || outcome === 'not_at_source' || outcome === 'already_enrolled' || outcome === 'source_already_advanced' || outcome === 'no_target') return 'warning';
    if (outcome === 'error') return 'danger';
    return 'default';
}

export default function JourneyEventLog({ campaignId, journeyId }: Props) {
    const [events, setEvents] = useState<JourneyTriggerEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(() => {
        if (!campaignId && !journeyId) return;
        setLoading(true);
        const req = campaignId ? getJourneyEvents(campaignId) : getProjectJourneyEvents(journeyId!);
        req
            .then(data => setEvents(data.events || []))
            .catch(() => setEvents([]))
            .finally(() => { setLoading(false); setLoaded(true); });
    }, [campaignId, journeyId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GlassButton variant="ghost" size="sm" onClick={load} title="Refresh">
                    {loading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RotateCw size={13} />}
                </GlassButton>
            </div>

            {loaded && events.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic', padding: '8px 0' }}>
                    No CRM lead-stage or lead-form trigger events recorded yet for this graph.
                </div>
            )}

            {events.map(ev => (
                <div
                    key={ev.event_id}
                    style={{
                        background: 'var(--panel-strong, var(--panel))',
                        border: '1px solid var(--stroke)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text)' }}>
                            {ev.trigger_type}{ev.match_value ? ` · ${ev.match_value}` : ''}
                        </span>
                        <GlassBadge variant={outcomeBadgeVariant(ev.outcome)}>{ev.outcome}</GlassBadge>
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                        {ev.recipient_email} · {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                    </div>
                    {ev.detail && (
                        <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            {ev.detail}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
